/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';
/*
 * Adds a gutter to the CodeMirror editor for error/warning/info
 * annotations.  Adds the property `annotations` to the component
 * to control displayed annotations.
 *
 * Prop `annotations` is an array of model/Annotation objects.
 *
 * !! The following MUST be imported from react-codemirror !!
 *   react-codemirror/static-1.5611/sass/annotations.sass
 *   codemirror/static-5.24/addon/scroll/simplescrollbars.css
 */

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'react-utils/debounce';
import getComponentName from 'react-utils/getComponentName';
import { isEqual, compose } from '../util/miniUnderscore.js';
import { Pos } from 'codemirror';
import 'codemirror/addon/scroll/annotatescrollbar';
import 'codemirror/addon/scroll/simplescrollbars';
import AnnotationType from 'react-codemirror/enum/AnnotationType';
import Annotation from 'react-codemirror/model/Annotation';
import { annotationsSorter, numberSorter } from 'react-codemirror/util/Sort';
import { loadCodeMirrorCSS, loadReactCodeMirrorCSS } from 'react-codemirror/util/LoadCSS';
import loadCSS from 'react-codemirror/decorator/_internal/loadCSS';
import UIIcon from 'UIComponents/icon/UIIcon';
import ScrollbarAnnotator from 'react-codemirror/decorator/_internal/components/annotations/ScrollbarAnnotator';
import SanitizedText from 'sanitize-text/components/SanitizedText';
const withLoadCSS = loadCSS([loadCodeMirrorCSS('/addon/scroll/simplescrollbars.css'), loadReactCodeMirrorCSS('/sass/annotations.css')]);
const POPULATE_DEBOUNCE_PERIOD = 200;
/*
 * Gutter annotation classes
 */

const GUTTER_ID = 'CodeMirror-annotation-gutter';
const GUTTER_HIGHLIGHT_GUTTER_CLASS = `${GUTTER_ID}-highlight-gutter`;
const GUTTER_HIGHLIGHT_LINE_CLASS = `${GUTTER_ID}-highlight-line`;
const MESSAGE_CSS_CLASS = `${GUTTER_ID}-message`;
const MESSAGE_CSS_OPEN_CLASS = `${MESSAGE_CSS_CLASS} open`;
const MESSAGE_MESSAGE_LIST_CLASS = `${MESSAGE_CSS_CLASS}-list`;
const MESSAGE_ITEM_CLASS = 'annotation-message-item';
const MESSAGE_ITEM_TYPE_CLASSES = {
  [AnnotationType.ERROR]: `${MESSAGE_ITEM_CLASS} error`,
  [AnnotationType.INFO]: `${MESSAGE_ITEM_CLASS} info`,
  [AnnotationType.WARNING]: `${MESSAGE_ITEM_CLASS} warning`
};
const GUTTER_MARKER_CSS_CLASS = `${GUTTER_ID}-marker`;
const GUTTER_MARKER_CSS_CLASSES = {
  [AnnotationType.ERROR]: `${GUTTER_MARKER_CSS_CLASS} error`,
  [AnnotationType.INFO]: `${GUTTER_MARKER_CSS_CLASS} info`,
  [AnnotationType.WARNING]: `${GUTTER_MARKER_CSS_CLASS} warning`
};
/*
 * Marker annotation classes
 */

const MARKED_TEXT_CLASSES = {
  [AnnotationType.ERROR]: 'cm-error',
  [AnnotationType.WARNING]: 'cm-hs-warning',
  [AnnotationType.INFO]: 'cm-hs-info'
};
const ICON_NAMES = {
  [AnnotationType.ERROR]: 'reportSpam',
  [AnnotationType.INFO]: 'info',
  [AnnotationType.WARNING]: 'warning'
};
export default function annotations() {
  return function withAnnotations(CodemirrorComponent) {
    /**
     * @class
     */
    class WithAnnotations extends Component {
      constructor(props) {
        super(props);
        /** @private */

        this.__annotations = {};
        /** @type {Object} CodeMirror instance taken from ref */

        this.codemirror = null;
        /** @type {HTMLElement} React container for gutter annotation tooltip */

        this.messageTooltip = null;
        /** @type {Array.<HTMLElement>} React containers for gutter annotations */

        this.gutterAnnotationContainers = [];
        /** @type {Array.<CodeMirror.TextMarker>} All markers added by this decorator */

        this.textMarkers = [];
        /** @type {Int} Currently selected message index in message tooltip */

        this.selectedMessageIndex = -1;
        /** @type {Int|Null} Currently highlighted gutter line number */

        this.highlightedGutterLineNumber = null;
        /** @type {Int|Null} Currently stickied gutter line number */

        this.stickiedGutterLineNumber = null;
        this.populateAnnotations = debounce(this.populateAnnotations.bind(this), POPULATE_DEBOUNCE_PERIOD);
        this.populateGutterAnnotation = this.populateGutterAnnotation.bind(this);
        this.onMouseEnterGutterAnnotation = this.onMouseEnterGutterAnnotation.bind(this);
        this.onMouseOutGutterAnnotation = this.onMouseOutGutterAnnotation.bind(this);
        this.onMouseMoveGutterAnnotation = this.onMouseMoveGutterAnnotation.bind(this);
        this.onClickGutterAnnotation = this.onClickGutterAnnotation.bind(this);
        this.onClickScrollbarAnnotation = this.onClickScrollbarAnnotation.bind(this);
        this.onChange = this.onChange.bind(this);
        this.refCodemirror = this.refCodemirror.bind(this);
      }

      componentDidUpdate(prevProps) {
        const hasPropChange = WithAnnotations.propTypesKeys.some(key => !isEqual(prevProps[key], this.props[key]));

        if (hasPropChange) {
          this.populateAnnotations(this.props.annotations);
        }
      }

      componentWillUnmount() {
        this.clearGutterAnnotations();
        this.removeMessageTooltip();
        this.removeScrollbarAnnotator();
        this.codemirror.off('change', this.onChange);
        this.codemirror = null;
        this.messageTooltip = null;
      }

      codemirrorDidMountOrUpdate(codemirror) {
        if (this.codemirror === codemirror) {
          return;
        }

        this.codemirror = codemirror;

        if (!this.codemirror) {
          return;
        }

        this.addMessageTooltip();
        this.addAnnotationGutter();
        this.addScrollbarAnnotator();
        this.populateAnnotations(this.props.annotations);
        this.codemirror.on('change', this.onChange);
      }

      addAnnotationGutter() {
        const gutters = this.codemirror.getOption('gutters');

        if (!~gutters.indexOf(GUTTER_ID)) {
          this.codemirror.setOption('gutters', [GUTTER_ID, ...gutters]);
        }
      }

      addMessageTooltip() {
        if (this.messageTooltip) {
          return;
        }

        if (!this.codemirror) {
          return;
        }

        this.messageTooltip = document.createElement('div');
        this.messageTooltip.className = MESSAGE_CSS_CLASS;
        const wrapperEl = this.codemirror.getWrapperElement();
        wrapperEl.appendChild(this.messageTooltip);
      }

      removeMessageTooltip() {
        if (!this.messageTooltip) {
          return;
        }

        ReactDOM.unmountComponentAtNode(this.messageTooltip);

        if (!this.codemirror) {
          return;
        }

        const wrapperEl = this.codemirror.getWrapperElement();
        wrapperEl.removeChild(this.messageTooltip);
      }

      addScrollbarAnnotator() {
        if (this.scrollbarAnnotator) {
          this.removeScrollbarAnnotator();
        }

        if (!(this.codemirror && this.codemirror.annotateScrollbar)) {
          return;
        }

        this.scrollbarAnnotator = new ScrollbarAnnotator(this.codemirror, this.onClickScrollbarAnnotation);
      }

      removeScrollbarAnnotator() {
        if (!this.scrollbarAnnotator) {
          return;
        }

        this.scrollbarAnnotator.destructor();
        this.scrollbarAnnotator = null;
      }
      /*
       * Annotations management
       */


      resetNormalizedAnnotations() {
        this.__annotations = {
          // Line numbers
          lines: [],
          // Annotations per line
          annos: {},
          // Types per line
          types: {}
        };
      }
      /**
       * Group annotations by line
       *
       * @param  {Annotation} anno
       * @return {Undefined}
       */


      normalizeAnnotation(anno) {
        const {
          lineNumber
        } = anno;

        if (this.__annotations.annos[lineNumber]) {
          this.__annotations.annos[lineNumber].push(anno);

          this.__annotations.types[lineNumber] |= anno.type;
        } else {
          this.__annotations.lines.push(lineNumber);

          this.__annotations.annos[lineNumber] = [anno];
          this.__annotations.types[lineNumber] = anno.type;
        }
      }
      /**
       * @return {Array.<UInt>}
       */


      getAnnotatedLines() {
        // The annotatescrollbar addon is buggy if the annos passed to
        // its update() methods are not sorted by line.
        return this.__annotations.lines.sort(numberSorter);
      }
      /**
       * @param  {UInt} lineNumber
       * @return {UInt.<AnnotationType>}
       */


      getAnnotationTypesByLine(lineNumber) {
        return this.__annotations.types[lineNumber];
      }
      /**
       * @param  {UInt} lineNumber
       * @return {Array.<Annotation>}
       */


      getAnnotationsByLine(lineNumber) {
        return this.__annotations.annos[lineNumber].sort(annotationsSorter);
      }
      /**
       * @param  {UInt32.<AnnotationType>} types
       * @return {AnnotationType}
       */


      getHighestPriorityType(types) {
        const {
          ERROR,
          WARNING,
          INFO
        } = AnnotationType;

        if (ERROR & types) {
          return ERROR;
        }

        if (WARNING & types) {
          return WARNING;
        }

        return INFO;
      }
      /**
       * @param  {UInt}           lineNumber
       * @param  {AnnotationType} type
       */


      populateGutterAnnotation(lineNumber, type) {
        if (!this.codemirror) {
          return;
        }

        const {
          disableAnnotationGutterMessages,
          disableAnnotationGutterNavigation,
          enableStickyAnnotations
        } = this.props;
        const annotationIsClickable = !disableAnnotationGutterNavigation || enableStickyAnnotations;
        const container = document.createElement('div');
        container.className = classNames(GUTTER_MARKER_CSS_CLASSES[type], annotationIsClickable && "clickable");
        ReactDOM.render( /*#__PURE__*/_jsx("div", {
          "data-linenumber": lineNumber,
          onMouseEnter: disableAnnotationGutterMessages ? null : this.onMouseEnterGutterAnnotation,
          onMouseOut: disableAnnotationGutterMessages ? null : this.onMouseOutGutterAnnotation,
          onMouseMove: disableAnnotationGutterMessages ? null : this.onMouseMoveGutterAnnotation,
          onClick: !annotationIsClickable ? null : this.onClickGutterAnnotation,
          children: "x"
        }), container);
        this.codemirror.doc.setGutterMarker(lineNumber, GUTTER_ID, container);
        this.gutterAnnotationContainers.push(container);
      }
      /**
       * @param  {Array.<Annotation>} annos
       * @return {Undefined}
       */


      populateAnnotations(annos) {
        if (!this.codemirror) {
          return;
        }

        const {
          enableAnnotationScrollbarMarkers
        } = this.props;
        this.codemirror.operation(() => {
          this.resetNormalizedAnnotations();
          this.clearGutterAnnotations();
          this.textMarkers.forEach(m => m.clear());
          this.textMarkers = [];

          if (!(annos && annos.length)) {
            this.scrollbarAnnotator.render(annos);
            return;
          }

          annos.forEach(anno => {
            this.markText(anno);
            this.normalizeAnnotation(anno);
          });
          const scrollAnnos = [];
          this.getAnnotatedLines().forEach(lineNumber => {
            const types = this.getAnnotationTypesByLine(lineNumber);
            const type = this.getHighestPriorityType(types);
            this.populateGutterAnnotation(lineNumber, type);

            if (this.scrollbarAnnotator && enableAnnotationScrollbarMarkers) {
              scrollAnnos.push({
                lineNumber,
                type
              });
            }
          });

          if (this.scrollbarAnnotator) {
            this.scrollbarAnnotator.render(scrollAnnos);
          }
        });
      }

      clearGutterAnnotations() {
        this.gutterAnnotationContainers.forEach(ReactDOM.unmountComponentAtNode);
        this.gutterAnnotationContainers = [];

        if (this.codemirror) {
          this.codemirror.doc.clearGutter(GUTTER_ID);
        }
      }
      /**
       * @param  {Annotation} annotation
       * @return {Object}
       */


      getAnnotationPosition({
        lineNumber,
        endLineNumber,
        column,
        endColumn,
        evidence
      }) {
        column = column || 0; // Single line with end column

        if (endLineNumber === lineNumber && endColumn > column) {
          const from = new Pos(lineNumber, column);
          const to = new Pos(lineNumber, endColumn);
          return {
            from,
            to
          };
        } // Multi line


        if (endLineNumber > lineNumber) {
          const from = new Pos(lineNumber, column);
          const to = new Pos(endLineNumber, endColumn || 0);
          return {
            from,
            to
          };
        } // Determine position from evidence


        if (evidence) {
          // BUG: Line could not exist if it was deleted just after annotations
          // were created and passed.
          const firstLineOfCode = this.codemirror.getLine(lineNumber);

          if (firstLineOfCode) {
            // Single line?
            const idx = firstLineOfCode.indexOf(evidence, column);

            if (~idx) {
              const from = new Pos(lineNumber, idx);
              const to = new Pos(lineNumber, Math.min(idx + evidence.length, firstLineOfCode.length));
              return {
                from,
                to
              };
            }
          } // Multi line?


          const evidenceLines = evidence.split(/\n/);

          if (evidenceLines.length > 1 && firstLineOfCode) {
            const beginColumn = firstLineOfCode.indexOf(evidenceLines[0], column);
            const from = new Pos(lineNumber, ~beginColumn ? beginColumn : 0);
            const lastLineOfCode = evidenceLines[evidenceLines.length - 1];
            const endLine = lineNumber + evidenceLines.length - 1;
            const to = new Pos(endLine, lastLineOfCode.length);
            return {
              from,
              to
            };
          }
        }

        const from = new Pos(lineNumber, column);
        const to = from;
        return {
          from,
          to
        };
      }

      markText(anno) {
        if (this.props.disableAnnotationTextMarkers) {
          return;
        }

        const {
          from,
          to
        } = this.getAnnotationPosition(anno);

        if (from.line === to.line && from.ch === to.ch) {
          // Noop since there is zero width
          return;
        }

        const {
          type,
          message
        } = anno;
        const options = {
          title: typeof message === 'string' ? message : '',
          className: MARKED_TEXT_CLASSES[type] || ''
        };
        this.textMarkers.push(this.codemirror.markText(from, to, options));
      }

      addOrRemoveGutterHighlight(lineNumber, add = true) {
        if (!this.codemirror) {
          return;
        }

        const method = add ? 'addLineClass' : 'removeLineClass';
        this.codemirror.doc[method](lineNumber, 'gutter', GUTTER_HIGHLIGHT_GUTTER_CLASS);
        this.codemirror.doc[method](lineNumber, 'background', GUTTER_HIGHLIGHT_LINE_CLASS);
      }

      activateLineFeedback(lineNumber) {
        if (lineNumber > this.codemirror.lineCount()) {
          return;
        }

        this.highlightedGutterLineNumber = lineNumber;
        this.addOrRemoveGutterHighlight(lineNumber, true);
        const cmWrapperEl = this.codemirror.getWrapperElement();
        const cmOffsetParent = cmWrapperEl.offsetParent;
        const gutterBg = cmWrapperEl.querySelector(`.CodeMirror-gutter-background.${GUTTER_HIGHLIGHT_GUTTER_CLASS}`); // Checking the parent's offset is necessary in case there is a transform
        // which will break the fixed positioning of this.messageTooltip

        const {
          left: parentLeft
        } = cmOffsetParent.getBoundingClientRect();
        const {
          bottom,
          right,
          left
        } = gutterBg.getBoundingClientRect(); // Check if the parent offset is the same as the codemirror offset,
        // if they are not equal, then the parent is breaking the fixed
        // positioning and the tooltip left must be adjusted

        const offsetForParent = left === parentLeft ? parentLeft : left - parentLeft;
        const tooltipLeft = offsetForParent + (right - left);
        const annos = this.getAnnotationsByLine(lineNumber); // If, on first click, the cursor is positioned within a marked error
        // then select that error.

        const cursorPos = this.codemirror.getCursor();
        const isAnnotationMarkFocused = cursorPos.line === lineNumber && annos.some((anno, idx) => {
          const {
            from,
            to
          } = this.getAnnotationPosition(anno);

          if (cursorPos.ch < from.ch || cursorPos.ch > to.ch) {
            return false;
          }

          this.selectedMessageIndex = idx;
          return true;
        });

        if (!isAnnotationMarkFocused) {
          this.selectedMessageIndex = -1;
        } // Render messages pane


        this.renderTooltipMessages(annos, this.selectedMessageIndex);
        this.messageTooltip.style.top = `${bottom}px`;
        this.messageTooltip.style.left = `${tooltipLeft}px`;
        this.messageTooltip.className = MESSAGE_CSS_OPEN_CLASS;
      }

      deactivateLineFeedback(lineNumber) {
        this.highlightedGutterLineNumber = null;
        this.addOrRemoveGutterHighlight(lineNumber, false);
        this.messageTooltip.className = MESSAGE_CSS_CLASS;
        ReactDOM.unmountComponentAtNode(this.messageTooltip);
        this.messageTooltip.style.top = '';
        this.messageTooltip.style.left = '';
      }

      navigateLineFeedback(lineNumber) {
        const annos = this.getAnnotationsByLine(lineNumber);
        this.selectedMessageIndex = this.selectedMessageIndex === annos.length - 1 ? 0 : this.selectedMessageIndex + 1;
        const selectedAnno = annos[this.selectedMessageIndex];
        const {
          from,
          to
        } = this.getAnnotationPosition(selectedAnno);
        this.codemirror.setSelection(to, from);

        if (!this.props.disableAnnotationGutterMessages) {
          this.renderTooltipMessages(annos, this.selectedMessageIndex);
        }
      }
      /*
       * Events
       */

      /**
       *
       */


      onChange() {
        if (this.highlightedGutterLineNumber != null) {
          this.deactivateLineFeedback(this.highlightedGutterLineNumber);
        }

        if (this.stickiedGutterLineNumber !== null) {
          this.deactivateLineFeedback(this.stickiedGutterLineNumber);
          this.stickiedGutterLineNumber = null;
        }
      }
      /**
       * @param  {SyntheticEvent} evt
       */


      onMouseEnterGutterAnnotation({
        currentTarget
      }) {
        const lineNumber = currentTarget.dataset.linenumber | 0;

        if (this.stickiedGutterLineNumber !== null && lineNumber !== this.stickiedGutterLineNumber) {
          this.deactivateLineFeedback(this.stickiedGutterLineNumber);
          this.stickiedGutterLineNumber = null;
        }

        this.activateLineFeedback(lineNumber);
      }
      /**
       * @param  {SyntheticEvent} evt
       */


      onMouseMoveGutterAnnotation({
        currentTarget
      }) {
        if (this.highlightedGutterLineNumber != null) {
          return;
        }

        const lineNumber = currentTarget.dataset.linenumber | 0;
        this.activateLineFeedback(lineNumber);
      }
      /**
       * @param  {SyntheticEvent} evt
       */


      onMouseOutGutterAnnotation({
        currentTarget
      }) {
        const lineNumber = currentTarget.dataset.linenumber | 0;

        if (this.stickiedGutterLineNumber !== null && lineNumber === this.stickiedGutterLineNumber) {
          return;
        }

        this.deactivateLineFeedback(lineNumber);
      }
      /**
       * @param  {SyntheticEvent} evt
       */


      onClickGutterAnnotation({
        currentTarget
      }) {
        const {
          disableAnnotationGutterNavigation,
          enableStickyAnnotations
        } = this.props;
        const lineNumber = currentTarget.dataset.linenumber | 0;

        if (!disableAnnotationGutterNavigation) {
          if (lineNumber !== this.highlightedGutterLineNumber) {
            if (this.highlightedGutterLineNumber != null) {
              this.deactivateLineFeedback(this.highlightedGutterLineNumber);
            }

            this.highlightedGutterLineNumber = lineNumber;
            this.activateLineFeedback(lineNumber);
          }

          this.navigateLineFeedback(lineNumber);
        }

        if (enableStickyAnnotations) {
          if (lineNumber === this.stickiedGutterLineNumber) {
            this.stickiedGutterLineNumber = null;
          } else {
            if (this.stickiedGutterLineNumber !== null) {
              this.deactivateLineFeedback(this.stickiedGutterLineNumber);
            }

            this.stickiedGutterLineNumber = lineNumber;
            this.activateLineFeedback(lineNumber);
          }
        }
      }
      /**
       * @param  {Number} lineNumber
       */


      onClickScrollbarAnnotation(lineNumber) {
        if (!this.codemirror) {
          return;
        }

        const lineAnnos = this.getAnnotationsByLine(lineNumber);
        const anno = lineAnnos && lineAnnos[0];

        if (!anno) {
          return;
        }

        const {
          from,
          to
        } = this.getAnnotationPosition(anno);

        if (from && to) {
          this.codemirror.setSelection(to, from);
          this.codemirror.focus();
        }
      }
      /*
       * Render
       */


      refCodemirror(ref) {
        const codemirror = ref && ref.codemirror;
        this.codemirrorDidMountOrUpdate(codemirror);

        if (this.props.refCodemirror) {
          this.props.refCodemirror(ref);
        }
      }
      /**
       * @param  {Array.<Annotation>} annos
       * @param  {Int}                selectedIndex
       */


      renderTooltipMessages(annos, selectedIndex) {
        const messages = annos.map(({
          type,
          message,
          lineNumber,
          column
        }, idx) => {
          const cx = classNames(MESSAGE_ITEM_TYPE_CLASSES[type], selectedIndex === idx && "selected");
          return /*#__PURE__*/_jsxs("li", {
            className: cx,
            children: [/*#__PURE__*/_jsx(UIIcon, {
              name: ICON_NAMES[type]
            }), " ", lineNumber + 1, ":", column, ' ', this.props.enableSanitizeAnnotationMessages ? /*#__PURE__*/_jsx(SanitizedText, {
              body: message,
              WrapperComponent: "span"
            }) : message]
          }, idx);
        });
        ReactDOM.render( /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx("ol", {
            className: MESSAGE_MESSAGE_LIST_CLASS,
            children: messages
          })
        }), this.messageTooltip);
      }

      render() {
        return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, this.props, {
          refCodemirror: this.refCodemirror
        }));
      }

    }
    /*
     * Statics
     */


    WithAnnotations.propTypes = {
      refCodemirror: PropTypes.func,

      /** @type {Array.<Annotation>} Array of annotations. */
      annotations: Annotation.annotationsPropType,

      /** @type {Boolean} Disables text correlating to annotations from being marked. */
      disableAnnotationTextMarkers: PropTypes.bool,

      /** @type {Boolean} Disables the message overlay for gutter annotations. */
      disableAnnotationGutterMessages: PropTypes.bool,

      /** @type {Boolean} Disables clicking gutter annotations to navigate correlating text. */
      disableAnnotationGutterNavigation: PropTypes.bool,

      /** @type {Boolean} Enables scrollbar annotations. */
      enableAnnotationScrollbarMarkers: PropTypes.bool,

      /** @type {Boolean} Enables clicking gutter annotations */
      enableStickyAnnotations: PropTypes.bool,

      /** @type {Boolean} Enables wrapping gutter annotation message with SanitizeText component */
      enableSanitizeAnnotationMessages: PropTypes.bool,

      /** @type {String} Default/recommended is `'overlay'`. Improves scroll annotations. */
      scrollbarStyle: PropTypes.string
    };
    WithAnnotations.defaultProps = {
      scrollbarStyle: 'overlay'
    };
    WithAnnotations.propTypesKeys = Object.freeze(Object.keys(WithAnnotations.propTypes));
    WithAnnotations.displayName = `WithAnnotations(${getComponentName(CodemirrorComponent)})`;
    return compose(withLoadCSS)(WithAnnotations);
  };
}